import React, { useEffect } from 'react'

import BannersPage from '../../../containers/marketing/banner/BannersPage'
import BannerTypesPage from '../../../containers/marketing/banner/BannerTypesPage'
import StoriesPage from '../../../containers/marketing/stories/StoriesPage'
import PromocodesPage from '../../../containers/marketing/promocode/PromocodesPage'
import LabelsPage from '../../../containers/marketing/label/LabelsPage'
import CommonErrorDisplay from '@components/common/CommonErrorDisplay'
import MainPageConfigsPage from 'containers/marketing/mainPageConfig/MainPageConfigsPage'
import LoyaltyCouponsPage from 'containers/marketing/loyaltyCoupons/LoyaltyCouponsPage'
import MarketingDashboard from '../dashboard/MarketingDashboard'
import { ROUTE_MARKETING_BANNER_ADD, ROUTE_MARKETING_BANNER_TYPE, ROUTE_MARKETING_BANNER_TYPE_ADD, ROUTE_MARKETING_BANNERS, ROUTE_MARKETING_LABEL_ADD, ROUTE_MARKETING_LOYALTY_COUPON_ADD, ROUTE_MARKETING_LOYALTY_COUPONS, ROUTE_MARKETING_MANAGEMENT, ROUTE_MARKETING_PROMOCODE_ADD, ROUTE_MARKETING_STORIES_ADD } from '@@partner:config/routes'

import CommonSubTopMenu from '@components/common/subTopMenu/CommonSubTopMenu'
import CommonTopMenu, { TopMenuElement } from '@components/common/CommonTopMenu'

import LegacyPlusIcon from '@rsuite/icons/legacy/Plus'
import LegacyListIcon from '@rsuite/icons/legacy/List'
import SiteIcon from '@rsuite/icons/Site'
import PlayOutlineIcon from '@rsuite/icons/PlayOutline'
import SiteParsingIcon from '@rsuite/icons/SiteParsing'
import TagNumberIcon from '@rsuite/icons/TagNumber'
import SpeakerIcon from '@rsuite/icons/Speaker'
import CouponIcon from '@rsuite/icons/Coupon'
import DashboardIcon from '@rsuite/icons/Dashboard'
import { generatePath } from 'react-router-dom'
import { usePartner } from '@@partner:helpers/hooks/auth'

export type MarketingStructure = 'bannerTypes' | 'mainPageConfigs' | 'promocodes' | 'banners' | 'stories' | 'labels' | 'loyaltyCoupons'


type ComponentConfig = {
  icon?: React.ReactNode
  title: string
  type: MarketingStructure
  hideOnTopMenu?: boolean
  component: React.FC<any>
  subPages?: {
    icon: React.ReactNode
    title: string
    to: string
  }[]
}

const componentsMap: Record<string, ComponentConfig> = {
  mainPageConfigs: {
    icon: <SiteIcon />,
    type: 'mainPageConfigs',
    title: 'Настройки главной страницы',
    component: MainPageConfigsPage
  },
  stories: {
    icon: <PlayOutlineIcon />,
    type: 'stories',
    title: 'Истории',
    component: StoriesPage,
    subPages: [
      {
        icon: <LegacyPlusIcon />,
        title: 'Добавить историю',
        to: ROUTE_MARKETING_STORIES_ADD
      }
    ]
  },
  banners: {
    icon: <SiteParsingIcon />,
    type: 'banners',
    title: 'Баннеры',
    component: BannersPage,
    subPages: [
      {
        icon: <SiteParsingIcon />,
        title: 'Баннеры',
        to: ROUTE_MARKETING_BANNERS
      },
      {
        icon: <LegacyListIcon />,
        title: 'Типы баннеров',
        to: ROUTE_MARKETING_BANNER_TYPE
      },
      {
        icon: <LegacyPlusIcon />,
        title: 'Добавить баннер',
        to: ROUTE_MARKETING_BANNER_ADD
      }
    ]
  },
  bannerTypes: {
    title: 'Типы баннеров',
    type: 'bannerTypes',
    hideOnTopMenu: true,
    component: BannerTypesPage,
    subPages: [
      {
        icon: <SiteParsingIcon />,
        title: 'Баннеры',
        to: ROUTE_MARKETING_BANNERS
      },
      {
        icon: <LegacyListIcon />,
        title: 'Типы баннеров',
        to: ROUTE_MARKETING_BANNER_TYPE
      },
      {
        icon: <LegacyPlusIcon />,
        title: 'Добавить тип баннера',
        to: ROUTE_MARKETING_BANNER_TYPE_ADD
      }
    ]
  },
  promocodes: {
    icon: <TagNumberIcon />,
    type: 'promocodes',
    title: 'Промокоды',
    component: PromocodesPage,
    subPages: [
      {
        icon: <LegacyPlusIcon />,
        title: 'Добавить промокод',
        to: ROUTE_MARKETING_PROMOCODE_ADD
      }
    ]
  },
  labels: {
    icon: <SpeakerIcon />,
    type: 'labels',
    title: 'Лейблы',
    component: LabelsPage,
    subPages: [
      {
        icon: <LegacyPlusIcon />,
        title: 'Новый лейбл',
        to: ROUTE_MARKETING_LABEL_ADD
      }
    ]
  },
  loyaltyCoupons: {
    icon: <CouponIcon />,
    type: 'loyaltyCoupons',
    title: 'Серии купонов лояльности',
    component: LoyaltyCouponsPage,
    subPages: [
      {
        icon: <LegacyPlusIcon />,
        title: 'Добавить серию купонов',
        to: ROUTE_MARKETING_LOYALTY_COUPON_ADD
      }
    ]
  }
}

export interface MarketingManagementUnitProps {
  type: string
  onChangePage?: (title: string) => void
}

export default function MarketingManagementUnit ({
  type = 'none',
  onChangePage
}: MarketingManagementUnitProps) {
  const { brandSlug } = usePartner()

  const { component: Component = null, title, subPages } = componentsMap?.[type] ?? {}

  useEffect(() => {
    if (title && onChangePage) {
      onChangePage(title)
    }
    else {
      onChangePage?.('Маркетинг')
    }
  }, [ title, onChangePage ])


  const topMenuElements: TopMenuElement[] = [
    {
      icon: <DashboardIcon />,
      to: generatePath(ROUTE_MARKETING_MANAGEMENT, { brandSlug, type: undefined }),
      title: 'Дашборд'
    },
    ...Object.entries(componentsMap)
      .filter(([ key, { hideOnTopMenu } ]) => !hideOnTopMenu)
      .map(([ key, { icon, title } ]) => ({
        icon,
        to: generatePath(ROUTE_MARKETING_MANAGEMENT, {
          brandSlug,
          type: key
        }),
        title
      }))
  ]


  if (!type) {
    return (
      <div>
        <CommonTopMenu elements={topMenuElements} />
        <MarketingDashboard />
      </div>
    )
  }

  if (!Component) {
    return (
      <CommonErrorDisplay
        title="Ошибка загрузки компонента"
        error={
          new Error(`Компонент для отображения запроса ${type} не найден!`)
        }
      />
    )
  }

  return (
    <div>
      <CommonTopMenu elements={topMenuElements} />
      { subPages && subPages.length > 0 ? (
        <CommonSubTopMenu
          elements={subPages}
        />
      ) : null }
      <Component />
    </div>
  )
}
